import { useState, useEffect, useRef } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';
import { getCache, setCache } from '@/utils/cache';

// Types for Jira board response
export interface JiraUser {
  name: string;
  email: string;
}

export interface JiraIssue {
  key: string;
  id: string;
  summary: string;
  description: string;
  status: {
    name: string;
    category: string;
  };
  assignee: JiraUser;
  reporter: JiraUser;
  priority: string;
  type: {
    name: string;
    iconUrl: string;
  };
  created: string;
  updated: string;
  duedate: string | null;
  labels: string[];
  storyPoints: number | null;
  sprint: string | null;
}

export interface JiraBoardData {
  success: boolean;
  board_id: string;
  total_issues: number;
  max_results: number;
  start_at: number;
  is_last: boolean;
  issues: JiraIssue[];
}

export const useJiraBoard = (): {
  data: JiraBoardData | null;
  loading: boolean;
  error: string | null;
} => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const [board, setBoard] = useState<JiraBoardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;

  useEffect(() => {
    if (fetchedRef.current) return;
    let isMounted = true;
    const cacheKey = 'jiraBoard_v1';
    const cached = getCache<JiraBoardData>(cacheKey);
    if (cached) {
      if (isMounted) {
        setBoard(cached);
        setError(null);
        setLoading(false);
        fetchedRef.current = true;
      }
      return;
    }

    const fetchBoard = async () => {
      const currentAccounts = accountsRef.current;
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        if (isMounted) {
          setError('REACT_APP_API_URL environment variable is not set');
          setLoading(false);
        }
        return;
      }
      const endpoint = `${apiUrl}/jira-board?board_id=1`;
      try {
        let token: string | null = null;
        if (isAuthenticated && currentAccounts.length > 0) {
          try {
            token = await acquireApiToken(instance, currentAccounts[0]);
          } catch (tokenError) {
            // Continue without token
          }
        }
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const response = await fetch(endpoint, { method: 'GET', headers });
        const raw = await response.json();
        // Some Lambda responses double-encode body
        let data: JiraBoardData | null = null;
        if (raw.body && typeof raw.body === 'string') {
          try {
            data = JSON.parse(raw.body);
          } catch {
            data = null;
          }
        } else {
          data = raw;
        }
        if (!response.ok || !(data && data.success)) {
          // Use error from raw response if present, otherwise fallback
          const errorMsg = (typeof raw.error === 'string' && raw.error)
            || (data && typeof data === 'object' && 'error' in data && typeof (data as any).error === 'string' && (data as any).error)
            || 'Failed to fetch Jira board';
          throw new Error(errorMsg);
        }
        if (isMounted) {
          setBoard(data);
          setError(null);
          try { setCache(cacheKey, data, 300); } catch {}
          fetchedRef.current = true;
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err?.message ?? 'Unknown error');
          setBoard(null);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchBoard();
    return () => { isMounted = false; };
  }, [isAuthenticated, instance]);
  return { data: board, loading, error };
};